import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { hasAnyRole } from '../config/roles.js';

/**
 * Gate for role-restricted routes — deny by default.
 *
 *   <Route element={<RoleRoute allow={[PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR]} />}>
 *     <Route path="users-roles" element={<UsersRolesPage />} />
 *   </Route>
 *
 * An anonymous visitor is redirected to login (preserving the intended
 * destination); a signed-in user without the role is *blocked* with the 403
 * page rather than merely having the nav entry hidden.
 *
 * This is the client-side half only — convenience, not security. Every gated
 * page's endpoints must enforce the same role server-side.
 */
export default function RoleRoute({ allow = [] }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!hasAnyRole(user, allow)) {
    return <Navigate to="/403" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
